// Обернуться on screen: while Волчица is in wolf form (view.statusOf(h).wolf — offline, online and replay
// views all answer it), her model is hidden and a big ink wolf (demonWolves.ts, twice a summoned wolf's size)
// runs in her place. The wolf hangs under her rig's root, so what follows the root carries over unchanged:
// position, facing and lift from driveHero, fog-of-war visibility, and the ring, bar and name placed by her
// position. The rig swaps and frees only its own content (heroView.ts), so a model mounting mid-form leaves
// the wolf alone; it is hidden here like any other content. Her mask look (ghost, shade, Наваждение's team
// swap — heroLook.ts) is put on the wolf when it appears and whenever it changes: fx.ts re-applies it to the
// rig only on its own changes. Taken off and freed the moment the form ends — death and a match reset end it
// too, since a dead hero is never in wolf form (so the dying mask never has to reach the wolf).
import * as THREE from 'three';
import { buildInkWolf, WOLF_STRIDE, type InkWolf } from './demonWolves.js';
import { runRate } from './gait.js';
import { release, setMaskLook } from './engrave.js';
import { heroMaskLook } from './heroLook.js';
import { viewerConfused } from './delusion.js';
import type { HeroView } from './heroView.js';
import type { GameView } from './gameView.js';

const SCALE = 2;

type Form = { wolf: InkWolf; clock: number; lookKey: string };
export type WolfForms = Map<number, Form>;
export function createWolfForms(): WolfForms { return new Map(); }

const toCamera = new THREE.Vector3(), at = new THREE.Vector3();

/** After driveHero: `t` is the view's clock (for the attack swing), `dt` the frame's. */
export function driveWolfForms(forms: WolfForms, heroViews: HeroView[], view: GameView, camera: THREE.Camera, t: number, dt: number) {
  const confused = viewerConfused(view);
  for (const hv of heroViews) {
    const h = hv.hero, root = hv.rig.root;
    let form = forms.get(h.id);
    if (!view.statusOf(h).wolf) {
      if (form) { drop(root, form); forms.delete(h.id); }
      continue;
    }
    if (!form) {
      form = { wolf: buildInkWolf(h.team, 1000 + h.id), clock: 0, lookKey: 'normal' };
      form.wolf.body.scale.setScalar(SCALE);
      root.add(form.wolf.body);
      forms.set(h.id, form);
    }
    const { look, swap, key } = heroMaskLook(view, h, confused);
    if (key !== form.lookKey) { setMaskLook(form.wolf.body, look, swap); form.lookKey = key; }
    for (const c of root.children) c.visible = c === form.wolf.body;
    form.clock += dt;
    const swing = Math.max(0, 1 - (t - h.swingAt) / 0.3);
    root.getWorldPosition(at);
    // paws keep pace with the ground: the speed driveHero measured for her rig
    const pace = hv.rig.pace?.speed;
    const gait = pace ? runRate(pace, WOLF_STRIDE * SCALE) : 1;
    form.wolf.animate(form.clock, view.movingOf(h), Math.sin(swing * Math.PI), toCamera.subVectors(camera.position, at).normalize(), gait);
  }
}

function drop(root: THREE.Object3D | null, form: Form) {
  form.wolf.body.removeFromParent();
  if (root) for (const c of root.children) c.visible = true;
  form.wolf.dispose();
  release(form.wolf.body);
}

/** Match stop: free every wolf still standing in for a hero. */
export function disposeWolfForms(forms: WolfForms, heroViews: HeroView[]) {
  for (const [id, form] of forms) drop(heroViews.find(v => v.hero.id === id)?.rig.root ?? null, form);
  forms.clear();
}
